import { ArrowRightIcon, Mail } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import {
  useCorrespondencePackageGetOrCreateForCoverageMutation,
  useCorrespondencePackageListQuery,
} from '@/services/api/correspondenceService';
import type { Task } from '@/services/types/client-intake-types';
import type { CoverageSchema } from '@/services/types/coverage-types';

interface TaskCorrespondenceActionsProps {
  task: Task;
  matterId: string;
  coverages?: CoverageSchema[];
  className?: string;
}

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  draft: { label: 'Draft', className: 'bg-gray-100 text-gray-700' },
  generating: { label: 'Generating', className: 'bg-blue-100 text-blue-700' },
  ready: { label: 'Ready', className: 'bg-green-100 text-green-700' },
  published: { label: 'Ready', className: 'bg-green-100 text-green-700' },
  error: { label: 'Error', className: 'bg-red-100 text-red-700' },
  missing_fields: {
    label: 'Missing Fields',
    className: 'bg-amber-100 text-amber-700',
  },
};

const TRANSMISSION_STATUS_CONFIG: Record<
  string,
  { label: string; className: string }
> = {
  pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-700' },
  sending: { label: 'Sending', className: 'bg-blue-100 text-blue-700' },
  sent: { label: 'Sent', className: 'bg-indigo-100 text-indigo-700' },
  delivered: { label: 'Delivered', className: 'bg-indigo-100 text-indigo-700' },
  failed: { label: 'Failed', className: 'bg-red-100 text-red-700' },
  bounced: { label: 'Bounced', className: 'bg-red-100 text-red-700' },
};

/**
 * Correspondence actions for tasks - handles creating/opening lien notice packages
 * Compact, card-style design matching CorrespondenceHistoryCard
 */
export const TaskCorrespondenceActions: React.FC<
  TaskCorrespondenceActionsProps
> = ({ task, matterId, coverages, className }) => {
  const navigate = useNavigate();
  const [getOrCreatePackage, { isLoading: isCreatingPackage }] =
    useCorrespondencePackageGetOrCreateForCoverageMutation();

  // Get coverage and carrier info
  const coverageId =
    (task.coverage?.id as string | undefined) || task.coverage_id;
  const fullCoverage = React.useMemo(
    () => coverages?.find((c) => c.id === coverageId),
    [coverages, coverageId],
  );
  const carrierId = fullCoverage?.carrier_v2_id;
  const carrierName = fullCoverage?.carrier?.name;

  // Fetch existing lien notice packages for this carrier
  const { data: existingPackages } = useCorrespondencePackageListQuery(
    {
      matterId,
      packageType: 'lien_notice',
      recipientCarrierId: carrierId ?? undefined,
    },
    { skip: !matterId || !carrierId },
  );

  const latestPackage = existingPackages?.[0]; // Packages are ordered by most recent

  // Determine status config - prioritize transmission status over render status
  const statusConfig = React.useMemo(() => {
    if (!latestPackage) return null;

    // If package has been transmitted, show transmission status
    const transmissionStatus = latestPackage.message?.transmission_status;
    if (transmissionStatus) {
      return (
        TRANSMISSION_STATUS_CONFIG[transmissionStatus] ||
        TRANSMISSION_STATUS_CONFIG.pending
      );
    }

    // Otherwise show render status
    return STATUS_CONFIG[latestPackage.status] || STATUS_CONFIG.draft;
  }, [latestPackage]);

  const canSend = !!matterId;

  const handleSendNotice = async () => {
    if (!canSend) return;

    try {
      // Get or create lien notice package with coverage context
      const result = await getOrCreatePackage({
        matterId,
        createCorrespondencePackage: {
          package_type: 'lien_notice',
          with_defaults: true,
          ...(carrierId && {
            recipient_entity_type: 'insurance_carrier' as const,
            recipient_entity_id: carrierId,
          }),
          ...(coverageId && { coverage_id: coverageId }),
        },
      }).unwrap();

      // Navigate to the correspondence page
      const returnUrl = `/matters/${matterId}/tasks`;
      navigate(
        `/matters/${matterId}/correspondence/${result.id}?returnUrl=${encodeURIComponent(returnUrl)}`,
      );
    } catch {
      toast.error('Failed to create lien notice');
    }
  };

  return (
    <button
      type="button"
      onClick={handleSendNotice}
      disabled={!canSend || isCreatingPackage}
      className={cn(
        'group flex w-full items-center justify-between gap-2 rounded-lg border p-2.5 transition-all',
        canSend && !isCreatingPackage
          ? 'border-indigo-200 bg-indigo-50/50 hover:border-indigo-300 hover:bg-indigo-50 active:bg-indigo-100'
          : 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60',
        className,
      )}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <div
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
            canSend ? 'bg-indigo-100 group-hover:bg-indigo-200' : 'bg-gray-100',
          )}
        >
          <Mail
            className={cn(
              'h-3.5 w-3.5',
              canSend ? 'text-indigo-600' : 'text-gray-400',
            )}
          />
        </div>
        <div className="text-left min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <p
              className={cn(
                'text-xs font-semibold',
                canSend ? 'text-gray-900' : 'text-gray-500',
              )}
            >
              {isCreatingPackage ? 'Creating...' : 'Draft Lien Notice'}
            </p>
            {statusConfig && (
              <span
                className={cn(
                  'inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                  statusConfig.className,
                )}
              >
                {statusConfig.label}
              </span>
            )}
          </div>
          <p className="text-[11px] text-gray-600 truncate mt-0.5">
            {carrierName ? `To: ${carrierName}` : 'Select recipient in editor'}
          </p>
        </div>
      </div>
      {canSend && !isCreatingPackage && (
        <ArrowRightIcon className="h-3.5 w-3.5 shrink-0 text-indigo-600 group-hover:translate-x-0.5 transition-transform" />
      )}
      {isCreatingPackage && (
        <div className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-indigo-600 border-r-transparent" />
      )}
    </button>
  );
};
